if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');
      this._syncingFromSticky = false;

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm && !theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });

      if (this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
      this.syncVariant();
    }

    syncVariant() {
      const stickyVariantInput = this.querySelector('[data-sticky-variant-id]');
      const stickySelect      = this.querySelector('[data-sticky-variant-select]');
      const addBtn            = this.querySelector('[name="add"]');
      const variantPicker     = this.productSection.querySelector('variant-picker');

      // Store for use in updateStickyUI
      this._variantPicker = variantPicker;
      this._addBtn        = addBtn;

      // ── Sticky select → main picker ────────────────────────────────────────
      if (stickySelect && variantPicker) {
        stickySelect.addEventListener('change', () => {
          const variantId = stickySelect.value;
          if (!variantId) return;

          // Update the hidden id input immediately
          if (stickyVariantInput) stickyVariantInput.value = variantId;

          // Update price & button in sticky bar immediately
          this.updateStickyUI(variantId);

          // Push selection to main variant picker
          this._syncingFromSticky = true;
          this.syncToMainPicker(variantPicker, variantId);
          setTimeout(() => { this._syncingFromSticky = false; }, 200);
        });
      }

      // ── Main picker → sticky bar ────────────────────────────────────────────
      document.addEventListener('on:variant:change', (event) => {
        const { variant } = event.detail;
        if (!variant) return;

        // Avoid feedback loop: when we triggered the change, skip select/input sync
        if (!this._syncingFromSticky) {
          if (stickyVariantInput) stickyVariantInput.value = variant.id;
          if (stickySelect)       stickySelect.value = String(variant.id);
        }

        // Always sync price and button state
        this.updateStickyUI(variant.id);
      });
    }

    /**
     * Updates price display and ATC button state in the sticky bar.
     * Reads pre-formatted price HTML from variantPicker.data.formatted (same source
     * used by the main product's updatePrice method in variant-picker.js).
     */
    updateStickyUI(variantId) {
      const vp = this._variantPicker;
      if (!vp || !vp.data) return;

      const variants  = (vp.data.product && vp.data.product.variants) || vp.data.variants;
      const formatted = vp.data.formatted;
      if (!variants || !formatted) return;

      const id      = Number(variantId);
      const variant = variants.find((v) => v.id === id);
      if (!variant) return;

      const fmtData = formatted[id];

      // ── Price elements ──────────────────────────────────────────────────────
      const priceEl = this.querySelector('.sticky-atc-price .price');
      if (priceEl && fmtData) {
        const isOnSale   = variant.compare_at_price > variant.price;
        const currentVal = priceEl.querySelector('.price__current .js-value');
        const wasVal     = priceEl.querySelector('.price__was .js-value');
        const wasLabel   = priceEl.querySelector('.price__was .js-label');

        if (currentVal) currentVal.innerHTML = fmtData.price || '';

        if (wasVal) {
          wasVal.innerHTML = isOnSale ? (fmtData.compareAtPrice || '') : '';
        }
        if (wasLabel) {
          wasLabel.textContent = isOnSale ? (theme.strings.regularPrice || '') : '';
        }

        priceEl.classList.toggle('price--on-sale',  isOnSale);
        priceEl.classList.toggle('price--sold-out', !variant.available);

        const defaultEl   = priceEl.querySelector('.price__default');
        const noVariantEl = priceEl.querySelector('.price__no-variant');
        if (defaultEl)   defaultEl.hidden   = false;
        if (noVariantEl) noVariantEl.hidden = true;
      }

      // ── ATC button ──────────────────────────────────────────────────────────
      const addBtn = this._addBtn;
      if (addBtn) {
        addBtn.disabled    = !variant.available;
        addBtn.textContent = variant.available
          ? (addBtn.dataset.addToCartText || 'Add to Cart')
          : (theme.strings.noStock || 'Sold Out');
      }
    }

    /**
     * Sets the main variant picker's option inputs to match the given variant,
     * then dispatches one change event so the picker updates price/media/URL.
     */
    syncToMainPicker(variantPicker, variantId) {
      const data = variantPicker.data;
      if (!data) return;

      const variants = (data.product && data.product.variants) || data.variants;
      if (!variants) return;

      const id            = Number(variantId);
      const targetVariant = variants.find((v) => v.id === id);
      if (!targetVariant) return;

      variantPicker.querySelectorAll('.option-selector').forEach((selector, index) => {
        const optionValue = targetVariant.options[index];
        if (optionValue === undefined) return;

        // Radio / button style
        selector.querySelectorAll('input[type="radio"]').forEach((radio) => {
          if (radio.value === optionValue) radio.checked = true;
        });

        // Native select / dropdown style
        const nativeSelect = selector.querySelector('select');
        if (nativeSelect) nativeSelect.value = optionValue;
      });

      // One event triggers the picker's full update logic
      variantPicker.dispatchEvent(new Event('change', { bubbles: false }));
    }

    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
